import { useEffect, RefObject } from "react"
import { Editor as TiptapEditor } from "@tiptap/react"

interface UseLineNumbersProps {
  editor: TiptapEditor | null
  lineNumbersEnabled: boolean
  editorContainerRef: RefObject<HTMLDivElement | null>
  lineNumbersRef: RefObject<HTMLDivElement | null>
}

/**
 * Hook to manage virtualized line numbers rendering for the editor
 */
export const useLineNumbers = ({
  editor,
  lineNumbersEnabled,
  editorContainerRef,
  lineNumbersRef,
}: UseLineNumbersProps): void => {
  useEffect(() => {
    // isDestroyed guard: tiptap v3 destroys/recreates the editor inside an
    // effect when its extensions change (every variant switch), so this
    // effect can re-run with an editor snapshot destroyed earlier in the
    // same commit — reading editor.view on it would throw.
    if (!editor || editor.isDestroyed || !lineNumbersEnabled) return

    const lineNumbersContainer = lineNumbersRef.current
    if (!lineNumbersContainer) return

    const editorScrollContainer = editorContainerRef.current
    if (!editorScrollContainer) return

    // Render visible line numbers with virtualization
    const renderLineNumbers = (): void => {
      const { scrollTop, clientHeight } = editorScrollContainer
      const scrollBottom = scrollTop + clientHeight

      const lines = editor.view.dom.querySelectorAll(
        ".ProseMirror > *"
      ) as NodeListOf<HTMLElement>
      const lineHeights: { height: number; top: number }[] = []
      let totalHeight = 0

      // Calculate cumulative heights to determine line positions
      lines.forEach((line: HTMLElement) => {
        const height = line.offsetHeight
        lineHeights.push({ height, top: totalHeight })
        totalHeight += height
      })

      // Find the visible lines
      const visibleLines: { index: number; top: number; height: number }[] = []
      for (let i = 0; i < lineHeights.length; i++) {
        const { top, height } = lineHeights[i]
        if (top + height >= scrollTop && top <= scrollBottom) {
          visibleLines.push({ index: i, top, height })
        }
        if (top > scrollBottom) {
          break
        }
      }

      // Clear existing line numbers
      lineNumbersContainer.innerHTML = ""

      // Render visible line numbers
      visibleLines.forEach(({ index, top, height }) => {
        const lineNumber = document.createElement("div")
        lineNumber.className = "line-number"
        lineNumber.textContent = (index + 1).toString()
        lineNumber.style.top = `${top}px`
        lineNumber.style.height = `${height}px`
        lineNumbersContainer.appendChild(lineNumber)
      })
    }

    // Initial render
    renderLineNumbers()

    // Scroll event listener with throttling
    let scrollTimeout: NodeJS.Timeout | null = null
    const handleScroll = (): void => {
      if (scrollTimeout) return
      scrollTimeout = setTimeout(() => {
        renderLineNumbers()
        scrollTimeout = null
      }, 50)
    }

    editorScrollContainer.addEventListener("scroll", handleScroll)
    editor.on("update", renderLineNumbers)

    return () => {
      editorScrollContainer.removeEventListener("scroll", handleScroll)
      editor.off("update", renderLineNumbers)
    }
  }, [editor, lineNumbersEnabled, editorContainerRef, lineNumbersRef])
}
